import React, { useState, useEffect, useRef, useMemo } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Text,
  VStack,
  Button,
  Flex,
  Spinner,
  useDisclosure,
  useToast,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
} from "@chakra-ui/react";
import VariantSidebar from "./VariantSidebar/VariantSidebar";
import SourceSection from "./VariantSidebar/Source/SourceSection";
import OutputSection from "./VariantSidebar/Output/OutputSection";
import {
  GetCompletedInventory,
  GetInventoryVersions,
  AddInventoryVersion
} from "../../InventoryServices/InventoryVersion/InventoryVersion";

const VariantTab = () => {
  const { id } = useParams();
  const [versions, setVersions] = useState([]);
  const [activeVersion, setActiveVersion] = useState(null);
  const [loading, setLoading] = useState(false);            // confirm button loading
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [lockedVersions, setLockedVersions] = useState(new Set()); // track confirmed/locked versions
  const [sourceApproved, setSourceApproved] = useState(false); // enables Add Output only after a source is approved
  const isMounted = useRef(true);
  const toast = useToast();

  // Confirm dialog
  const {
    isOpen: isConfirmOpen,
    onOpen: onConfirmOpen,
    onClose: onConfirmClose
  } = useDisclosure();
  const cancelRef = useRef(null);

  // Fetch versions from API
  const fetchVersions = async () => {
    try {
      if (!isMounted.current) return;
      setVersionsLoading(true);
      const res = await GetInventoryVersions(id);
      if (isMounted.current) {
        setVersions(res || []);
        if ((res || []).length > 0 && !activeVersion) {
          setActiveVersion(res[0].id);
        }
        // map any server-side "is_locked" to local locked set
        const locked = new Set();
        (res || []).forEach(v => {
          if (v?.is_locked) locked.add(v.id);
        });
        setLockedVersions(locked);
      }
    } catch (err) {
      console.error("Error fetching versions:", err);
      toast({
        title: "Failed to load versions",
        status: "error",
        duration: 3000,
        isClosable: true,
      }); // Chakra toast feedback pattern [web:125][web:12]
    } finally {
      if (isMounted.current) setVersionsLoading(false);
    }
  };

  // Add new version handler with toast error display
  const handleAddVersion = async () => {
    try {
      const res = await AddInventoryVersion(id);
      // If API returns {status:false, message:"Only completed inventory can have new version"}
      if (res?.status === false) {
        toast({
          title: "Unable to add version",
          description: res?.message || "Failed to add version",
          status: "error",
          duration: 3000,
          isClosable: true,
        }); // show backend message in toast [web:125][web:12]
        return;
      }
      await fetchVersions(); // refresh list on success
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to add version";
      toast({
        title: "Unable to add version",
        description: msg,
        status: "error",
        duration: 3000,
        isClosable: true,
      }); // unified error toast handling [web:125][web:12]
    }
  };

  // Open confirm dialog when clicking the button
  const onConfirmDesignClick = () => {
    if (!activeVersion) return;
    onConfirmOpen(); // AlertDialog open via useDisclosure
  };

  // Confirm design API call (only from AlertDialog Confirm)
  const handleConfirmDesign = async () => {
    if (!activeVersion) return;
    setLoading(true);
    try {
      await GetCompletedInventory(activeVersion); // server marks version completed/locked
      // reflect lock locally to disable edits
      setLockedVersions(prev => {
        const next = new Set(prev);
        next.add(activeVersion);
        return next;
      });
      toast({
        title: "Design confirmed",
        description: `Version ${activeVersion} is now locked.`,
        status: "success",
        duration: 2000,
        isClosable: true,
      }); // success toast per Chakra recommendations [web:125][web:12]
      onConfirmClose();
    } catch (err) {
      console.error("Error confirming design:", err);
      toast({
        title: "Failed to confirm",
        description: "Please try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
      }); // error toast path [web:125][web:12]
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch on mount
  useEffect(() => {
    isMounted.current = true;
    if (id) {
      fetchVersions();
    }
    return () => {
      isMounted.current = false;
    };
  }, [id]);

  // Current version and locked flag
  const currentVersion = useMemo(
    () => versions.find(v => v.id === activeVersion),
    [versions, activeVersion]
  );
  const isActiveLocked = lockedVersions.has(activeVersion);

  return (
    <Box display="flex" bg="gray.50" minHeight="500px" w="100%" maxW="100%" overflowX="hidden">
      {/* Sidebar Component */}
      <VariantSidebar
        versions={versions}
        activeVersion={activeVersion}
        setActiveVersion={setActiveVersion}
        onAddVersion={handleAddVersion}
        loading={versionsLoading}
        canAddVersion={true}
      />

      {/* Main Content Area */}
      <Box flex="1" p={8} minW={0}>
        <Flex
          justify="space-between"
          align={{ base: "flex-start", sm: "center" }}
          direction={{ base: "column", sm: "row" }}
          gap={3}
          mb={6}
        >
          <Text fontWeight="bold" fontSize="xl" color="blue.600">
            {currentVersion
              ? `Design Variants - Version ${currentVersion.version}${isActiveLocked ? " (Locked)" : ""}`
              : "Design Variants - Select a Version"}
          </Text>
          <Button
            colorScheme="blue"
            size="sm"
            onClick={onConfirmDesignClick}
            isDisabled={!activeVersion || isActiveLocked}
            isLoading={loading}
            loadingText="Confirming..."
            flexShrink={0}
          >
            Confirm Design
          </Button> {/* isLoading shows spinner inside Button */}
        </Flex>

        {activeVersion ? (
          <VStack spacing={10} align="stretch">
            <SourceSection
              versionId={activeVersion}
              isLocked={isActiveLocked}
              onApprovalStatusChange={setSourceApproved}
            />
            <OutputSection
              versionId={activeVersion}
              isLocked={isActiveLocked}
              sourceApproved={sourceApproved}
            />
          </VStack>
        ) : (
          <Box textAlign="center" py={10}>
            <Text color="gray.500">
              {versionsLoading ? "Loading versions..." : "Please select or create a version"}
            </Text>
          </Box>
        )}
      </Box>

      {/* Confirm AlertDialog */}
      <AlertDialog
        isOpen={isConfirmOpen}
        leastDestructiveRef={cancelRef}
        onClose={onConfirmClose}
        isCentered
      >
        <AlertDialogOverlay />
        <AlertDialogContent>
          <AlertDialogHeader fontSize="lg" fontWeight="bold">
            Confirm Design
          </AlertDialogHeader>

          <AlertDialogBody>
            If confirmed, this version will be locked and no further updates will be allowed in this version. Do you want to proceed?
          </AlertDialogBody>

          <AlertDialogFooter>
            <Button ref={cancelRef} onClick={onConfirmClose}>
              Cancel
            </Button>
            <Button colorScheme="blue" onClick={handleConfirmDesign} ml={3} isLoading={loading}>
              Confirm
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog> {/* AlertDialog pattern per Chakra docs [web:87] */}
    </Box>
  );
};

export default VariantTab;